import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, SkipForward, Globe, Mail, Phone, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/Layout";
import { api, errMsg, fullName, ago } from "@/lib/api";

const href = (w) => (w?.startsWith("http") ? w : `https://${w}`);

function LeadCard({ lead }) {
  return (
    <div className="bg-white border rounded-2xl p-6 lg:p-8 space-y-5 fade-up" key={lead.id} data-testid="review-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="label-xs mb-1">{lead.business_categories?.join(" · ") || "No category"} · {lead.source || "No source"}</div>
          <h2 className="text-2xl font-extrabold tracking-tight" data-testid="review-company">{lead.company_name}</h2>
          <div className="text-slate-600 mt-1">{fullName(lead) || "No contact name"}</div>
        </div>
        <Link to={`/leads/${lead.id}`} className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1" data-testid="review-open-lead">Open full record <ExternalLink size={13} /></Link>
      </div>
      <div className="flex flex-wrap gap-4 text-sm text-slate-700">
        <span className="inline-flex items-center gap-1.5"><Mail size={14} className="text-slate-400" />{lead.email}</span>
        {lead.telephone && <span className="inline-flex items-center gap-1.5"><Phone size={14} className="text-slate-400" />{lead.telephone}</span>}
        {lead.website && <a href={href(lead.website)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-600 hover:underline" data-testid="review-website"><Globe size={14} />{lead.website}</a>}
      </div>
      {lead.ai_description ? <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line max-h-60 overflow-y-auto">{lead.ai_description}</p>
        : <p className="text-sm text-slate-400">No company description yet. Open the full record to generate one.</p>}
      {lead.notes?.length > 0 && <div className="text-xs text-slate-500 border-t pt-3">Latest note: {lead.notes[0]?.text}</div>}
      <div className="text-xs text-slate-400">Added {ago(lead.created_at)}</div>
    </div>
  );
}

export default function Review() {
  const [data, setData] = useState(null);
  const [skipped, setSkipped] = useState([]);
  const [camps, setCamps] = useState([]);
  const [cid, setCid] = useState(() => localStorage.getItem("sn_review_campaign") || "none");
  const [busy, setBusy] = useState(false);
  const load = useCallback((skip) => api.get("/review/next", { params: { skip: skip.join(",") } }).then((r) => setData(r.data)), []);
  useEffect(() => { api.get("/campaigns").then((r) => setCamps(r.data)); load([]); }, [load]);
  useEffect(() => { localStorage.setItem("sn_review_campaign", cid); }, [cid]);
  const lead = data?.lead;
  const act = useCallback(async (action) => {
    if (!lead || busy) return;
    if (action === "skip") { const s = [...skipped, lead.id]; setSkipped(s); load(s); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post(`/leads/${lead.id}/review`, { action, campaign_id: action === "approve" && cid !== "none" ? cid : null });
      toast.success(action === "reject" ? `${lead.company_name} rejected` : `${lead.company_name} → 1 · Cold Email${r.queued ? " · first email queued in Outbox" : ""}`);
      window.dispatchEvent(new Event("outbox-changed"));
      await load(skipped);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }, [lead, busy, skipped, cid, load]);
  useEffect(() => {
    const h = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey || ["INPUT", "TEXTAREA"].includes(e.target.tagName) || e.target.isContentEditable) return;
      const k = e.key.toLowerCase();
      if (k === "a") act("approve"); else if (k === "r") act("reject"); else if (k === "s") act("skip");
    };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, [act]);
  return (
    <div>
      <PageHeader eyebrow="CRM" title="Review queue">
        <span className="label-xs self-center" data-testid="review-remaining">{data ? `${data.remaining} not reviewed` : ""}</span>
      </PageHeader>
      <div className="p-6 lg:p-10 max-w-4xl space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <span className="label-xs">On approve, also add to</span>
          <Select value={cid} onValueChange={setCid}><SelectTrigger className="w-72" data-testid="review-campaign-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">No campaign (just mark ready)</SelectItem>{camps.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.status})</SelectItem>)}</SelectContent></Select>
          {cid !== "none" && <span className="text-xs text-slate-500">The first email is queued in the Outbox. Nothing is sent automatically.</span>}
        </div>
        {!data ? <div className="label-xs">Loading…</div> : lead ? (
          <>
            <LeadCard lead={lead} />
            <div className="flex flex-wrap gap-3">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act("approve")} data-testid="review-approve-btn"><Check size={16} className="mr-1" />Approve <kbd className="ml-2 text-[10px] opacity-70">A</kbd></Button>
              <Button size="lg" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" disabled={busy} onClick={() => act("reject")} data-testid="review-reject-btn"><X size={16} className="mr-1" />Reject <kbd className="ml-2 text-[10px] opacity-70">R</kbd></Button>
              <Button size="lg" variant="ghost" disabled={busy} onClick={() => act("skip")} data-testid="review-skip-btn"><SkipForward size={16} className="mr-1" />Skip for now <kbd className="ml-2 text-[10px] opacity-70">S</kbd></Button>
            </div>
          </>
        ) : (
          <div className="bg-white border rounded-2xl p-10 text-center space-y-3" data-testid="review-empty">
            <div className="text-lg font-bold">{skipped.length ? "You've been through every lead in the queue" : "All caught up"}</div>
            <p className="text-sm text-slate-500">{skipped.length ? `${skipped.length} skipped lead(s) are still Not Reviewed.` : "There are no Not Reviewed leads right now."}</p>
            {skipped.length > 0 && <Button variant="outline" onClick={() => { setSkipped([]); load([]); }} data-testid="review-restart-btn">Review skipped leads</Button>}
          </div>
        )}
      </div>
    </div>
  );
}
